import React, { useState } from "react";
import PropTypes from "prop-types";
import { postData } from "../../../FetchApiServices";
import { useToast } from "../Layout/T2TUIProvider";

/**
 * T2TVendorActions \u2014 Lifecycle action buttons for vendor detail page.
 * Each action creates a workflow request for approval.
 *
 * Props:
 *  - vendor: object (full vendor record)
 *  - onActionComplete: () => void (refresh vendor data)
 */
const T2TVendorActions = ({ vendor, onActionComplete }) => {
  const toast = useToast();
  const [modal, setModal] = useState(null); // null | "edit" | "block" | "unblock" | "deactivate" | "kyc"
  const [form, setForm] = useState({ reason: "", change_type: "", fields_changed: {} });
  const [submitting, setSubmitting] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  if (!vendor) return null;

  const isActive = vendor.status === "active";
  const isBlocked = vendor.status === "blocked";
  const isPendingKyc = vendor.status === "pending_rekyc";

  const closeModal = () => { setModal(null); setForm({ reason: "", change_type: "", fields_changed: {} }); setCheckResult(null); };

  // ---- Create Edit Request ----
  const handleEditRequest = async () => {
    if (!form.change_type) { toast("Select what you want to change.", "warning"); return; }
    if (!form.reason.trim()) { toast("Reason is required.", "warning"); return; }
    setSubmitting(true);
    const resp = await postData(`t2t/vendors/${vendor.vendor_id}/edit-request`, {
      change_type: form.change_type,
      reason: form.reason.trim(),
      fields_changed: form.fields_changed,
    });
    if (resp.error) { toast(resp.error.message || "Failed to create edit request.", "error"); }
    else { toast("Edit request created. It will go through the approval workflow.", "success"); closeModal(); if (onActionComplete) onActionComplete(); }
    setSubmitting(false);
  };

  // ---- Block Vendor ----
  const handleBlock = async () => {
    if (form.reason.trim().length < 10) { toast("Please provide a detailed reason (min 10 characters).", "warning"); return; }
    setSubmitting(true);
    const resp = await postData(`t2t/vendors/${vendor.vendor_id}/block`, { reason: form.reason.trim() });
    if (resp.error) { toast(resp.error.message || "Failed to initiate block.", "error"); }
    else { toast("Block request created. Pending approval.", "success"); closeModal(); if (onActionComplete) onActionComplete(); }
    setSubmitting(false);
  };

  // ---- Unblock Vendor ----
  const handleUnblock = async () => {
    if (form.reason.trim().length < 10) { toast("Resolution note is required (min 10 characters).", "warning"); return; }
    setSubmitting(true);
    const resp = await postData(`t2t/vendors/${vendor.vendor_id}/unblock`, { reason: form.reason.trim() });
    if (resp.error) { toast(resp.error.message || "Failed to initiate unblock.", "error"); }
    else { toast("Unblock request created. Pending approval.", "success"); closeModal(); if (onActionComplete) onActionComplete(); }
    setSubmitting(false);
  };

  // ---- Deactivate Vendor ----
  const handleDeactivate = async () => {
    if (form.reason.trim().length < 10) { toast("Reason is required (min 10 characters).", "warning"); return; }
    setSubmitting(true);
    // Pre-check for open POs/invoices
    const checkResp = await postData(`t2t/vendors/${vendor.vendor_id}/deactivate`, { reason: form.reason.trim(), check_only: true });
    if (checkResp.error) {
      if (checkResp.error.open_pos || checkResp.error.open_invoices) {
        setCheckResult({ open_pos: checkResp.error.open_pos || 0, open_invoices: checkResp.error.open_invoices || 0 });
        setSubmitting(false);
        return;
      }
      toast(checkResp.error.message || "Failed to deactivate.", "error");
      setSubmitting(false);
      return;
    }
    // Proceed with deactivation
    const resp = await postData(`t2t/vendors/${vendor.vendor_id}/deactivate`, { reason: form.reason.trim() });
    if (resp.error) { toast(resp.error.message || "Failed to deactivate.", "error"); }
    else { toast("Deactivation request created. Pending approval.", "success"); closeModal(); if (onActionComplete) onActionComplete(); }
    setSubmitting(false);
  };

  // ---- Force KYC ----
  const handleForceKyc = async () => {
    setSubmitting(true);
    const resp = await postData(`t2t/vendors/${vendor.vendor_id}/force-kyc`, {});
    if (resp.error) { toast(resp.error.message || "Failed to trigger KYC.", "error"); }
    else { toast("KYC renewal workflow created.", "success"); closeModal(); if (onActionComplete) onActionComplete(); }
    setSubmitting(false);
  };

  const CHANGE_TYPES = [
    { value: "name_change", label: "Name Change" },
    { value: "address_change", label: "Address Change" },
    { value: "bank_change", label: "Bank Details Change" },
    { value: "tax_change", label: "Tax / Compliance Change" },
    { value: "contact_change", label: "Contact Information Change" },
    { value: "document_renewal", label: "Document Renewal" },
  ];

  return (
    <>
      {/* Action Buttons */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {(isActive || isPendingKyc) && (
          <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={() => setModal("edit")}>
            {"\u270F\uFE0F"} Create Edit Request
          </button>
        )}
        {isActive && (
          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ color: "#DC2626", borderColor: "#DC2626" }} onClick={() => setModal("block")}>
            {"\uD83D\uDEAB"} Block Vendor
          </button>
        )}
        {isBlocked && (
          <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={() => setModal("unblock")}>
            {"\u2705"} Unblock Vendor
          </button>
        )}
        {(isActive || isBlocked) && (
          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ color: "#64748B" }} onClick={() => setModal("deactivate")}>
            {"\u26D4"} Deactivate
          </button>
        )}
        {isActive && (
          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => setModal("kyc")}>
            {"\uD83D\uDD04"} Force KYC Review
          </button>
        )}
      </div>

      {/* ---- MODALS ---- */}
      {modal && (
        <div className="t2t-overlay" onClick={closeModal}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="t2t-modal-head">
              <div className="t2t-modal-title">
                {modal === "edit" && "\u270F\uFE0F Edit Vendor Request"}
                {modal === "block" && "\uD83D\uDEAB Block Vendor"}
                {modal === "unblock" && "\u2705 Unblock Vendor"}
                {modal === "deactivate" && "\u26D4 Deactivate Vendor"}
                {modal === "kyc" && "\uD83D\uDD04 Force KYC Review"}
              </div>
              <button className="t2t-close-btn" onClick={closeModal}>{"\u00D7"}</button>
            </div>
            <div className="t2t-modal-body">
              {/* Vendor info */}
              <div style={{ padding: 10, background: "#F8FAFC", borderRadius: 6, marginBottom: 14, fontSize: 12 }}>
                <strong>{vendor.vendor_number}</strong> {"\u2014"} {vendor.name}
                <span style={{ marginLeft: 8, padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: isActive ? "#F0FDF4" : isBlocked ? "#FEF2F2" : "#FFF7ED", color: isActive ? "#16A34A" : isBlocked ? "#DC2626" : "#D97706" }}>
                  {vendor.status?.toUpperCase()}
                </span>
              </div>

              {/* Edit — change type selector */}
              {modal === "edit" && (
                <div className="t2t-fg" style={{ marginBottom: 12 }}>
                  <label>What do you want to change? *</label>
                  <select className="t2t-fc" value={form.change_type} onChange={(e) => setForm({ ...form, change_type: e.target.value })}>
                    <option value="">Select change type...</option>
                    {CHANGE_TYPES.map((ct) => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                  </select>
                </div>
              )}

              {/* Reason / Note */}
              {modal !== "kyc" && (
                <div className="t2t-fg">
                  <label>
                    {modal === "edit" && "Reason for change *"}
                    {modal === "block" && "Reason for blocking * (min 10 chars)"}
                    {modal === "unblock" && "Resolution note * (min 10 chars)"}
                    {modal === "deactivate" && "Reason for deactivation * (min 10 chars)"}
                  </label>
                  <textarea className="t2t-fc" rows={3} value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    placeholder={
                      modal === "block" ? "e.g. Legal dispute, fraud suspected, compliance failure..."
                        : modal === "unblock" ? "e.g. Issue resolved, vendor cleared, re-audit completed..."
                          : modal === "deactivate" ? "e.g. Vendor went out of business, relationship ended..."
                            : "Describe the change needed..."
                    } />
                </div>
              )}

              {/* KYC confirmation */}
              {modal === "kyc" && (
                <div style={{ fontSize: 13, color: "var(--t2t-text-secondary)", lineHeight: 1.7 }}>
                  This will immediately create a KYC renewal workflow for <strong>{vendor.name}</strong>.
                  PAN, GST, and Bank verifications will be re-run. The vendor&apos;s KYC schedule will be updated.
                </div>
              )}

              {/* Deactivation pre-check warning */}
              {checkResult && (
                <div style={{ padding: 12, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, marginTop: 12, fontSize: 12, color: "#DC2626" }}>
                  <strong>Cannot deactivate:</strong> This vendor has {checkResult.open_pos > 0 ? `${checkResult.open_pos} open PO(s)` : ""}{checkResult.open_pos > 0 && checkResult.open_invoices > 0 ? " and " : ""}{checkResult.open_invoices > 0 ? `${checkResult.open_invoices} pending invoice(s)` : ""}.
                  Resolve these before deactivation.
                </div>
              )}

              {/* Block warning */}
              {modal === "block" && (
                <div style={{ padding: 10, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 6, marginTop: 12, fontSize: 11, color: "#9A3412" }}>
                  Blocking will prevent purchase orders and payments against this vendor in OSIF and SAP.
                </div>
              )}
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={closeModal}>Cancel</button>
              <button
                className={`t2t-btn ${modal === "block" || modal === "deactivate" ? "t2t-btn-danger" : "t2t-btn-primary"}`}
                onClick={modal === "edit" ? handleEditRequest : modal === "block" ? handleBlock : modal === "unblock" ? handleUnblock : modal === "deactivate" ? handleDeactivate : handleForceKyc}
                disabled={submitting || !!checkResult}
              >
                {submitting ? "Processing..." : modal === "edit" ? "Create Edit Request" : modal === "block" ? "Block Vendor" : modal === "unblock" ? "Unblock Vendor" : modal === "deactivate" ? "Deactivate" : "Trigger KYC"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

T2TVendorActions.propTypes = {
  vendor: PropTypes.object.isRequired,
  onActionComplete: PropTypes.func,
};

/**
 * T2TDocExpiryBadge \u2014 Color-coded document expiry indicator.
 */
export const T2TDocExpiryBadge = ({ expiryDate }) => {
  if (!expiryDate) return <span style={{ fontSize: 10, color: "#94A3B8" }}>No expiry</span>;
  const now = new Date();
  const exp = new Date(expiryDate);
  const daysLeft = Math.ceil((exp - now) / 86400000);

  let color, bg, border, label;
  if (daysLeft < 0) {
    color = "#DC2626"; bg = "#FEF2F2"; border = "#FECACA"; label = `Expired ${Math.abs(daysLeft)}d ago`;
  } else if (daysLeft <= 30) {
    color = "#DC2626"; bg = "#FEF2F2"; border = "#FECACA"; label = `Expires in ${daysLeft}d`;
  } else if (daysLeft <= 90) {
    color = "#D97706"; bg = "#FFF7ED"; border = "#FED7AA"; label = `Expires in ${daysLeft}d`;
  } else {
    color = "#16A34A"; bg = "#F0FDF4"; border = "#BBF7D0"; label = `Valid (${daysLeft}d)`;
  }

  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, color, background: bg, border: `1px solid ${border}` }}>
      {label}
    </span>
  );
};

T2TDocExpiryBadge.propTypes = { expiryDate: PropTypes.string };

/**
 * T2TKycBadge \u2014 KYC status indicator for vendor list/detail.
 */
export const T2TKycBadge = ({ vendor }) => {
  if (!vendor) return null;
  const status = vendor.status;
  const nextKyc = vendor.next_kyc_due;

  if (status === "pending_rekyc") {
    return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA" }}>KYC OVERDUE</span>;
  }

  if (!nextKyc) return null;

  const daysLeft = Math.ceil((new Date(nextKyc) - new Date()) / 86400000);
  if (daysLeft <= 0) {
    return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA" }}>KYC DUE</span>;
  }
  if (daysLeft <= 60) {
    return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: "#FFF7ED", color: "#D97706", border: "1px solid #FED7AA" }}>KYC in {daysLeft}d</span>;
  }
  if (daysLeft <= 90) {
    return <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "#F8FAFC", color: "#64748B", border: "1px solid #E2E8F0" }}>KYC in {daysLeft}d</span>;
  }
  return null;
};

T2TKycBadge.propTypes = { vendor: PropTypes.object };

export default T2TVendorActions;
